/**
 * Column Flattening for CSV Import/Export
 *
 * Builds a flat list of importable CSV headers driven by columnConfig.
 * Handles entity refs (manual mode) with elementMapping expansion,
 * compound sellPriceUI sub-fields, formula exclusion, protected column
 * exclusion, and composite field expansion.
 *
 * `columnConfig` carries the one meaning it has everywhere (SYD-2279):
 * `undefined` is "every schema column, in schema order", `[]` is "no columns at
 * all" — the same reading `applyColumnConfig` gives the XLSX export. Callers must
 * pass the layout element's raw prop; collapsing `undefined` into `[]` yields an
 * empty template.
 *
 * Consumed by `downloadTemplateCsv` and `useArTableCsvImport`. Dot-notation
 * sub-fields (entity mapped fields, composites, sellPriceUI) are placed
 * immediately after their parent column.
 */

import { UIElementType } from '#types/System.types';
import { NUMERIC_FIELD_DATA_TYPES } from '#types/System.FieldTypes.types';
import type { FieldType, UIArTableColumnOverride, CompositeFieldType } from '#types/System.types';
import type {
	ArTableType,
	ArColumnDefinition,
	ArColumnReference,
	TypeId,
} from '#types/System.Artifacts.types';

type CsvDataType = 'string' | 'number' | 'boolean' | 'date' | 'dateTime' | 'reference' | 'skip';

type CsvFlatColumn = {
	header: string;
	typeId: TypeId;
	subFieldPath?: string;
	targetTypeId?: TypeId;
	dataType: CsvDataType;
	importable: boolean;
};

const SELL_PRICE_SUB_FIELDS = ['basePrice', 'ratePercent', 'sellPrice'] as const;

const BOOLEAN_FIELD_DATA_TYPES = new Set([
	'yesNo',
]);

const SKIP_FIELD_DATA_TYPES = new Set([
	'image', 'file', 'richText',
]);

/**
 * `date` and `dateTime` are separate `CsvDataType`s because they anchor
 * differently on import (SYD-2507). Only those two scalars get a date type:
 * `time`, `dateRange` and the other ranges resolve to `'string'`, which the
 * importer passes through untouched.
 */
function resolveDataType(
	fieldDataTypeId: string | undefined,
): CsvDataType {
	if (!fieldDataTypeId) return 'string';
	if (NUMERIC_FIELD_DATA_TYPES.has(fieldDataTypeId)) return 'number';
	if (fieldDataTypeId === 'date') return 'date';
	if (fieldDataTypeId === 'dateTime') return 'dateTime';
	if (BOOLEAN_FIELD_DATA_TYPES.has(fieldDataTypeId)) return 'boolean';
	if (SKIP_FIELD_DATA_TYPES.has(fieldDataTypeId)) return 'skip';
	return 'string';
}

function getCompositeSubFieldIds(composite: CompositeFieldType): string[] {
	const structure = composite.defaultUILayout?.structure;
	if (!structure || !('children' in structure)) return [];
	const children = (structure as { children?: unknown[] }).children;
	if (!Array.isArray(children)) return [];
	return children
		.filter((c): c is { fieldId: string } => typeof (c as { fieldId?: unknown }).fieldId === 'string')
		.map((c) => c.fieldId);
}

function collectElementMappingTargets(
	schema: ArTableType,
	columnConfig: UIArTableColumnOverride[],
): Set<TypeId> {
	const columnMap = new Map(
		(schema.columns ?? []).map((col) => [col.typeId, col]),
	);
	const consumed = new Set<TypeId>();

	for (const { typeId, excludeFromImport } of columnConfig) {
		if (excludeFromImport) continue;
		const column = columnMap.get(typeId);
		if (!column || column.uiElementType !== UIElementType.Reference) continue;

		const refColumn = column as ArColumnReference;
		if (!refColumn.elementMapping || refColumn.elementMapping.length === 0) continue;

		for (const mapping of refColumn.elementMapping) {
			if (mapping.source.type === 'field' && mapping.target.type === 'column') {
				consumed.add(mapping.target.id as TypeId);
			}
		}
	}

	return consumed;
}

function getElementMappingFieldTargets(
	refColumn: ArColumnReference,
	schemaColumns: ArColumnDefinition[],
	fieldTypes: Record<string, FieldType>,
	formulaSet: Set<string>,
	protectedSet: Set<TypeId>,
): CsvFlatColumn[] {
	if (!refColumn.elementMapping || refColumn.elementMapping.length === 0) return [];

	const schemaOrder = new Map(schemaColumns.map((col, idx) => [col.typeId, idx]));
	const refTypeId = refColumn.typeId;

	const mapped: Array<{ targetId: TypeId; order: number }> = [];
	for (const mapping of refColumn.elementMapping) {
		if (mapping.source.type !== 'field' || mapping.target.type !== 'column') continue;
		const targetId = mapping.target.id as TypeId;
		if (protectedSet.has(targetId)) continue;
		if (formulaSet.has(targetId)) continue;
		const order = schemaOrder.get(targetId) ?? Infinity;
		mapped.push({ targetId, order });
	}

	mapped.sort((a, b) => a.order - b.order);

	return mapped.map(({ targetId }) => {
		const fieldType = fieldTypes[targetId];
		return {
			header: `${refTypeId}.${targetId}`,
			typeId: refTypeId,
			targetTypeId: targetId,
			dataType: resolveDataType(fieldType?.fieldDataTypeId),
			importable: true,
		};
	});
}

function flattenColumnsForCsv(
	schema: ArTableType,
	fieldTypes: Record<string, FieldType>,
	columnConfig: UIArTableColumnOverride[] | undefined,
	compositeFieldTypes: CompositeFieldType[],
): CsvFlatColumn[] {
	const schemaColumns = schema.columns ?? [];
	const columnMap = new Map(schemaColumns.map((col) => [col.typeId, col]));
	const effectiveColumnConfig: UIArTableColumnOverride[] =
		columnConfig ?? schemaColumns.map((column) => ({ typeId: column.typeId }));

	const formulaSet = new Set<string>(
		(schema.formulaSequence ?? []).map((e) => e.typeId),
	);

	const protectedSet = new Set<TypeId>(
		schema.protected?.protectedTypeIds ?? [],
	);

	const compositeMap = new Map(
		compositeFieldTypes.map((cft) => [cft.docId, cft]),
	);

	const consumedByMapping = collectElementMappingTargets(schema, effectiveColumnConfig);

	const result: CsvFlatColumn[] = [];

	for (const configEntry of effectiveColumnConfig) {
		const { typeId } = configEntry;

		if (configEntry.excludeFromImport) continue;

		const column = columnMap.get(typeId);
		if (!column) continue;

		if (protectedSet.has(typeId)) continue;

		if (column.uiElementType === UIElementType.Reference) {
			result.push({
				header: typeId,
				typeId,
				dataType: 'reference',
				importable: true,
			});

			const refColumn = column as ArColumnReference;
			const mappedFields = getElementMappingFieldTargets(
				refColumn, schemaColumns, fieldTypes, formulaSet, protectedSet,
			);
			result.push(...mappedFields);
			continue;
		}

		if (column.uiElementType !== UIElementType.Field) continue;

		if (consumedByMapping.has(typeId)) continue;

		if (formulaSet.has(typeId)) continue;

		const fieldType = fieldTypes[typeId];
		const fieldDataTypeId = fieldType?.fieldDataTypeId;

		if (fieldDataTypeId === 'sellPriceUI') {
			for (const subField of SELL_PRICE_SUB_FIELDS) {
				const dottedId = `${typeId}.${subField}`;
				if (formulaSet.has(dottedId)) continue;

				result.push({
					header: `${typeId}.${subField}`,
					typeId,
					subFieldPath: `fields.${subField}.value`,
					dataType: 'number',
					importable: true,
				});
			}
			continue;
		}

		if (fieldDataTypeId === 'composite') {
			const composite = compositeMap.get(typeId);
			if (!composite) continue;

			const subFieldIds = getCompositeSubFieldIds(composite);
			for (const subFieldId of subFieldIds) {
				const dottedId = `${typeId}.${subFieldId}`;
				if (formulaSet.has(dottedId)) continue;

				const subFieldType = fieldTypes[subFieldId];
				const subDataType = resolveDataType(subFieldType?.fieldDataTypeId);

				result.push({
					header: `${typeId}.${subFieldId}`,
					typeId,
					subFieldPath: `fields.${subFieldId}.value`,
					dataType: subDataType,
					importable: true,
				});
			}
			continue;
		}

		result.push({
			header: typeId,
			typeId,
			dataType: resolveDataType(fieldDataTypeId),
			importable: true,
		});
	}

	return result;
}

export { flattenColumnsForCsv };
export type { CsvFlatColumn, CsvDataType };
